import { Grid, Tooltip } from "@mui/material";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { observer } from "mobx-react";
import React from "react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import { useStandardConfigurationBuilder } from "../LLMConfigurationBuilderContext";
import { LLMModelSelector } from "../LLMModelSelector";
import { LLMPromptTemplateSelector } from "../LLMPromptTemplateSelector";
import { ModifyPromptTemplate } from "../ModifyPromptTemplate";
import { ResultDefinitionSection } from "./ResultDefinitionSection";
import { LLMClassifierBuilderStepperStore } from "../../Stores/LLMClassifierBuilderStepperStore";

interface StandardBuilderProps {}

export const StandardBuilder = observer(function (props: StandardBuilderProps) {
    const store = useStore(LLMClassifierBuilderStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);
    const builder = useStandardConfigurationBuilder();

    return (
        <Grid container direction="column" rowGap={2}>
            <Grid container item columns={2} columnGap={2} flexWrap="nowrap">
                <Grid item xs={1}>
                    <LLMModelSelector
                        inputLabel="LLM Model"
                        required
                        defaultValue={store.classifier.implementation.llmModel}
                        error={stepper.hasError.llmModelMissing}
                        onChange={(model) => {
                            store.classifier.implementation.setLLMModel(model);
                            stepper.resetError("llmModelMissing");
                        }}
                    />
                </Grid>

                <Grid item xs={1}>
                    <LLMPromptTemplateSelector
                        inputLabel="LLM Prompt Template"
                        llmResultDefinitions={builder.llmResultDefinitions}
                        getValue={(templates) => {
                            return templates.find((template) => {
                                return (
                                    template.id ===
                                    builder.llmPromptTemplateOption?.id
                                );
                            });
                        }}
                        error={stepper.hasError.llmPromptTemplateMissing}
                        onChange={(template) => {
                            builder.setLLMPromptTemplate(template?.template);
                            builder.setLLMPromptTemplateOption(template);
                            stepper.resetError("llmPromptTemplateMissing");
                        }}
                    />
                </Grid>
            </Grid>

            <Grid container item columns={2} columnGap={2} flexWrap="nowrap">
                <Tooltip title="The text in this input will replace the {{question}} template variable in the prompt template.">
                    <Grid item xs={1}>
                        <AcxMainTextField
                            id="question-text"
                            labelText="Question Text"
                            required
                            fullWidth
                            multiline
                            multilinePadding
                            rowsMin={3}
                            rowsMax={5}
                            error={
                                stepper.hasError.questionTextMissing &&
                                !builder.questionText
                            }
                            hideErrorIcon
                            value={builder.questionText}
                            onChange={(event) => {
                                builder.setQuestionText(event.target.value);
                            }}
                        />
                    </Grid>
                </Tooltip>
                <Tooltip title="The text in this input will replace the {{definition}} template variable in the prompt template.">
                    <Grid item xs={1}>
                        <AcxMainTextField
                            id="question-definition"
                            labelText="Question Definition"
                            required
                            fullWidth
                            multiline
                            multilinePadding
                            rowsMin={3}
                            rowsMax={5}
                            error={
                                stepper.hasError.questionDefinitionMissing &&
                                !builder.questionDefinition
                            }
                            hideErrorIcon
                            value={builder.questionDefinition}
                            onChange={(event) => {
                                builder.setQuestionDefinition(
                                    event.target.value,
                                );
                            }}
                        />
                    </Grid>
                </Tooltip>
            </Grid>

            {builder.llmResultDefinitions &&
                builder.llmResultDefinitions.length > 0 &&
                builder.llmResultDefinitions.map((definition) => (
                    <Grid item key={definition.result}>
                        <ResultDefinitionSection
                            llmResultDefinition={definition}
                        />
                    </Grid>
                ))}
            <Grid item>
                <ModifyPromptTemplate
                    defaultValue={builder.llmPromptTemplate}
                    onSave={(template) =>
                        builder.setLLMPromptTemplate(template)
                    }
                />
            </Grid>
        </Grid>
    );
});
